import { Link, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CalendarDays } from 'lucide-react'
import { toast } from 'sonner'

import { assignmentsApi } from '@/services/assignments'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty } from '@/components/ui/empty'
import {
  isAssignmentStatusBlockingWorkDayEntryStatusChange,
  resolveAssignmentStatus,
  isTerminalAssignmentStatus,
} from '@/lib/assignment-status'
import {
  getWorkDayEntryStatusBadgeClassName,
  getWorkDayEntryStatusLabel,
  resolveWorkDayEntryStatusFromEntry,
  type WorkDayEntryStatus,
} from '@/lib/workday-entry-status'
import { isJobModificationBlocked } from '@/lib/job-status'
import { getErrorMessage } from '@/lib/api'
import { withServiceInstanceId } from '@/lib/service-instance'
import { formatCurrency, formatDate } from '@/lib/utils'
import { WorkDayEntryActionsMenu } from '@/components/workdays/work-day-entry-actions-menu'
import { useAuthStore } from '@/stores/auth-store'
import type { Assignment, Job } from '@/types'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'

type JobWorkDayEntriesPanelProps = {
  jobId: string
  serviceInstanceId?: string | null
  job?: Pick<Job, 'status' | 'is_locked'> | null
  syncSelectedDateInUrl?: boolean
}

const EMBEDDED_ENTRY_LIMIT = 5

const getWorkDateKey = (value?: string | null) => {
  if (!value) {
    return null
  }

  const trimmed = value.trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10)
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  const localDate = new Date(parsed)
  localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset())
  return localDate.toISOString().slice(0, 10)
}

const resolveAssignmentForEntry = (
  assignments: readonly Assignment[],
  jobId: string,
  workerId: string,
): Assignment | undefined => {
  const relevant = assignments
    .filter((assignment) => String(assignment.job_id ?? assignment.job ?? '') === jobId
      && String(assignment.worker_id ?? assignment.worker ?? '') === workerId)
    .sort((left, right) => Number(right.id ?? 0) - Number(left.id ?? 0))

  if (!relevant.length) {
    return undefined
  }

  return relevant.find(
    (assignment) => !isAssignmentStatusBlockingWorkDayEntryStatusChange(assignment.status),
  ) ?? relevant[0]
}

const formatHours = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed)) {
    return String(value)
  }

  return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(2).replace(/\.?0+$/, '')
}

const formatMinutesAsHours = (value?: number | string | null) => {
  const minutes = Number(value)
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return '-'
  }

  return formatHours(minutes / 60)
}

const resolveEstimatedHours = (entry: { planned_duration_minutes?: number; planned_start?: string | null; planned_end?: string | null }) => {
  if (entry.planned_duration_minutes) {
    return formatMinutesAsHours(entry.planned_duration_minutes)
  }

  if (!entry.planned_start || !entry.planned_end) {
    return '-'
  }

  const start = new Date(entry.planned_start)
  const end = new Date(entry.planned_end)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return '-'
  }

  return formatMinutesAsHours((end.getTime() - start.getTime()) / 60000)
}

const resolveLoggedHours = (entry: {
  actual_duration_minutes?: number
  check_in?: string | null
  check_out?: string | null
}) => {
  if (entry.actual_duration_minutes) {
    return formatMinutesAsHours(entry.actual_duration_minutes)
  }

  if (entry.check_in && entry.check_out) {
    const checkIn = new Date(entry.check_in)
    const checkOut = new Date(entry.check_out)
    if (!Number.isNaN(checkIn.getTime()) && !Number.isNaN(checkOut.getTime()) && checkOut > checkIn) {
      return formatMinutesAsHours((checkOut.getTime() - checkIn.getTime()) / 60000)
    }
  }

  return '-'
}

const formatMoney = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? formatCurrency(parsed) : String(value)
}

const normalizePayTypeLabel = (value?: string | null) => {
  const normalized = String(value ?? '').trim().replace(/[_-]+/g, ' ')
  if (!normalized) {
    return 'Pay'
  }

  return normalized
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const resolveAssignmentPayAmount = (assignment?: Assignment | null) => {
  if (!assignment) {
    return null
  }

  const candidates = [
    assignment.pay_scale_base_pay,
    assignment.pay,
    assignment.pay_flat,
  ]

  return candidates.find((value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0
  }) ?? null
}

const formatPayAgreed = (assignment?: Assignment | null) => {
  if (!assignment) {
    return '-'
  }

  const rawPayType = String(assignment.pay_type ?? '').trim().toLowerCase()
  const payTypeLabel = assignment.pay_type_label || normalizePayTypeLabel(rawPayType)
  if (rawPayType.includes('month') || payTypeLabel.toLowerCase().includes('month')) {
    return 'Monthly'
  }

  const amount = resolveAssignmentPayAmount(assignment)
  if (amount === null || amount === undefined) {
    return payTypeLabel
  }

  return `${payTypeLabel} · ${formatMoney(amount)}`
}

const isWorkerInvoicePayType = (payType?: string | null) => ['per_hour', 'per_job'].includes(String(payType ?? '').trim())

const formatConflictTime = (value?: string | null) => {
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatLateMinutes = (value?: number | string | null) => {
  const totalMinutes = Math.floor(Number(value ?? 0))
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return 'Late'
  }

  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60
  const parts: string[] = []

  if (days > 0) {
    parts.push(`${days}d`)
  }
  if (hours > 0) {
    parts.push(`${hours}h`)
  }
  if (minutes > 0 || parts.length === 0) {
    parts.push(`${minutes}m`)
  }

  return `Late ${parts.join(' ')}`
}

const shouldHideClashForAssignment = (status?: string | null) => {
  const resolved = resolveAssignmentStatus(status)
  return resolved === 'rejected' || resolved === 'withdrawn' || resolved === 'completed'
}

const shouldHideClashForWorkdayEntryStatus = (status?: string | null) => {
  const normalized = String(status ?? '').trim().toLowerCase()
  return normalized === 'completed' || normalized === 'rejected'
}

const getApprovedModifierLabels = (entry: {
  night_pay_approved?: boolean
  holiday_approved?: boolean
  peak_time_approved?: boolean
  special_day_approved?: boolean
  over_time_approved?: boolean
}) => [
  entry.night_pay_approved ? 'Night' : null,
  entry.holiday_approved ? 'Holiday' : null,
  entry.peak_time_approved ? 'Peak' : null,
  entry.special_day_approved ? 'Special' : null,
  entry.over_time_approved ? 'Overtime' : null,
].filter((label): label is string => Boolean(label))

const formatRatingSummary = (entry: {
  rating?: number | string | null
  timely_arrived_rating?: number | string | null
  quality_work_rating?: number | string | null
  conduct_rating?: number | string | null
}) => {
  const parts = [
    entry.rating ? `Overall ${entry.rating}/5` : null,
    entry.timely_arrived_rating ? `Punctuality ${entry.timely_arrived_rating}/5` : null,
    entry.quality_work_rating ? `Performance (Quality) ${entry.quality_work_rating}/5` : null,
    entry.conduct_rating ? `Conduct ${entry.conduct_rating}/5` : null,
  ].filter((part): part is string => Boolean(part))

  return parts.join(' · ')
}

const patchWorkDayEntryStatusInCache = (
  current: unknown,
  entryId: string,
  status: string,
) => {
  if (!current || typeof current !== 'object') {
    return current
  }

  const record = current as Record<string, unknown>
  if (!Array.isArray(record.results)) {
    return current
  }

  const nextStatusLabel = getWorkDayEntryStatusLabel(status)

  return {
    ...record,
    results: record.results.map((item) => {
      if (!item || typeof item !== 'object') {
        return item
      }

      const row = item as Record<string, unknown>
      if (String(row.id ?? '') !== String(entryId)) {
        return item
      }

      return {
        ...row,
        status,
        status_label: nextStatusLabel,
      }
    }),
  }
}

export function JobWorkDayEntriesPanel({
  jobId,
  serviceInstanceId,
  job,
  syncSelectedDateInUrl = false,
}: JobWorkDayEntriesPanelProps) {
  const labels = useWorkspaceLabels()
  const queryClient = useQueryClient()
  const canGroupWorkdayStatusOverride = useAuthStore(
    (state) => Boolean(state.currentPost?.capabilities?.can_access_group_workday_access),
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedWorkDate = getWorkDateKey(searchParams.get('date'))
  const focusedWorkerId = (searchParams.get('worker_id') || '').trim()
  const focusedAssignmentId = (searchParams.get('assignment_id') || '').trim()
  const hasWorkdayFocus = Boolean(focusedWorkerId || focusedAssignmentId)
  const [selectedWorkDate, setSelectedWorkDate] = useState(requestedWorkDate ?? 'all')
  const entriesQuery = useQuery({
    queryKey: ['job-work-day-entries', jobId],
    queryFn: () => assignmentsApi.getWorkDayEntries({ job_id: jobId, page_size: 200 }),
    enabled: Boolean(jobId),
    staleTime: 30_000,
  })

  const assignmentsQuery = useQuery({
    queryKey: ['job-assignments', jobId],
    queryFn: () => assignmentsApi.getAssignments({ job_id: jobId, page_size: 200 }),
    enabled: Boolean(jobId),
    staleTime: 30_000,
  })

  const assignments = assignmentsQuery.data?.results ?? []
  const jobModificationBlocked = isJobModificationBlocked(job)
  const setWorkDayEntryStatusMutation = useMutation({
    mutationFn: ({
      id,
      status,
      assignmentId,
      checkIn,
      checkOut,
      reason,
    }: {
      id: string
      status: WorkDayEntryStatus
      assignmentId?: string | number | null
      checkIn?: string
      checkOut?: string
      reason?: string
    }) =>
      assignmentsApi.setWorkDayEntryStatus(id, status, {
        assignmentId,
        checkIn,
        checkOut,
        reason,
      }),
    onMutate: ({ id, status }) => {
      queryClient.setQueryData(['job-work-day-entries', jobId], (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, id, status),
      )
      queryClient.setQueryData(['workday-entries'], (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, id, status),
      )
    },
    onSuccess: async (_data, variables) => {
      toast.success('Work day entry status updated')
      queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry-audits'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry', String(variables.id)] })
      queryClient.invalidateQueries({ queryKey: ['job-workflow-transitions', 'workday_entry', String(variables.id)] })
      queryClient.invalidateQueries({ queryKey: ['job-cancellation-reasons', 'workday_entry', String(variables.id)] })
      queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['assignment-workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['attention-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      await entriesQuery.refetch()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const entries = entriesQuery.data?.results ?? []
  const entriesWithAssignment = entries.map((entry) => {
    const workerId = String(entry.worker ?? '')
    const entryJobId = String(entry.job ?? jobId)
    const entryAssignmentId = String((entry as { assignment?: string | number | null; assignment_id?: string | number | null }).assignment ?? (entry as { assignment_id?: string | number | null }).assignment_id ?? '')
    const assignment = entryAssignmentId
      ? assignments.find((candidate) => String(candidate.id) === entryAssignmentId)
      : resolveAssignmentForEntry(assignments, entryJobId, workerId)
    return { entry, assignment }
  })
  const focusedEntriesWithAssignment = entriesWithAssignment.filter(({ entry, assignment }) => {
    if (focusedWorkerId && String(entry.worker ?? '') !== focusedWorkerId) {
      return false
    }
    const entryAssignmentId = String(
      (entry as { assignment?: string | number | null; assignment_id?: string | number | null }).assignment
        ?? (entry as { assignment_id?: string | number | null }).assignment_id
        ?? '',
    )
    if (
      focusedAssignmentId
      && entryAssignmentId
      && entryAssignmentId !== focusedAssignmentId
      && String(assignment?.id ?? '') !== focusedAssignmentId
    ) {
      return false
    }
    return true
  })
  const workDayDateLabels = Array.from(
    new Set(
      focusedEntriesWithAssignment
        .map(({ entry }) => entry.work_date)
        .filter((workDate): workDate is string => Boolean(workDate))
    )
  )
  const firstWorkDate = workDayDateLabels[0] ?? 'all'
  const effectiveWorkDateFilter = selectedWorkDate === 'all' && firstWorkDate !== 'all' && !hasWorkdayFocus
    ? firstWorkDate
    : selectedWorkDate === 'all' || workDayDateLabels.includes(selectedWorkDate)
    ? selectedWorkDate
    : firstWorkDate
  const filteredEntriesWithAssignment = effectiveWorkDateFilter === 'all'
    ? focusedEntriesWithAssignment
    : focusedEntriesWithAssignment.filter(({ entry }) => entry.work_date === effectiveWorkDateFilter)
  const visibleEntriesWithAssignment = hasWorkdayFocus
    ? filteredEntriesWithAssignment
    : filteredEntriesWithAssignment.slice(0, EMBEDDED_ENTRY_LIMIT)
  const hiddenEntryCount = hasWorkdayFocus
    ? 0
    : Math.max(filteredEntriesWithAssignment.length - visibleEntriesWithAssignment.length, 0)
  const clashEntries = filteredEntriesWithAssignment.filter(({ entry, assignment }) => {
    if (shouldHideClashForAssignment(assignment?.status)) {
      return false
    }
    if (shouldHideClashForWorkdayEntryStatus(entry.status)) {
      return false
    }
    return Number(entry.overlap_conflict_count ?? 0) > 0
  })
  const workdaysHref = withServiceInstanceId(
    `/workdays?job=${encodeURIComponent(jobId)}`,
    serviceInstanceId,
  )
  const selectedWorkdayCalendarHref = withServiceInstanceId(
    effectiveWorkDateFilter === 'all'
      ? `/workdays?job=${encodeURIComponent(jobId)}`
      : `/workdays?job=${encodeURIComponent(jobId)}&date=${encodeURIComponent(effectiveWorkDateFilter)}`,
    serviceInstanceId,
  )

  useEffect(() => {
    setSelectedWorkDate(requestedWorkDate ?? 'all')
  }, [requestedWorkDate])

  useEffect(() => {
    if (!syncSelectedDateInUrl || effectiveWorkDateFilter === 'all' || requestedWorkDate === effectiveWorkDateFilter) {
      return
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('date', effectiveWorkDateFilter)
    setSearchParams(nextParams, { replace: true })
  }, [effectiveWorkDateFilter, requestedWorkDate, searchParams, setSearchParams, syncSelectedDateInUrl])

  const handleWorkDateFilterChange = (workDate: string) => {
    setSelectedWorkDate(workDate)

    if (syncSelectedDateInUrl) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('date', workDate)
      setSearchParams(nextParams, { replace: true })
    }
  }

  const clearWorkdayFocus = () => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('worker_id')
    nextParams.delete('assignment_id')
    setSearchParams(nextParams, { replace: true })
  }

  if (entriesQuery.isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    )
  }

  if (!entries.length) {
    return (
      <Empty className="py-10">
        <CalendarDays className="mb-4 h-10 w-10 text-muted-foreground/60" />
        <p className="text-lg font-medium">No {labels.workDayEntryPluralLower} yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Schedule workers and log hours from the {labels.workDayPluralLower} calendar.
        </p>
        <Button className="mt-4" asChild>
          <Link to={workdaysHref}>
            <CalendarDays className="mr-2 h-4 w-4" />
            Open {labels.workDayPluralLower}
          </Link>
        </Button>
      </Empty>
    )
  }

  return (
    <div className="space-y-3">
      {hasWorkdayFocus ? (
        <div className="flex flex-col gap-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-primary">
            Showing workday entries
            {focusedWorkerId ? ` for worker #${focusedWorkerId}` : ''}
            {focusedAssignmentId ? ` on assignment #${focusedAssignmentId}` : ''}.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={clearWorkdayFocus}>
            Show all entries
          </Button>
        </div>
      ) : null}

      {clashEntries.length > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <div className="flex gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
            <div>
              <p className="font-medium">
                {clashEntries.length} schedule{clashEntries.length === 1 ? ' has' : 's have'} worker schedule clashes.
              </p>
              <p className="text-xs text-amber-900/80">
                Overlaps are allowed, but review the clashing schedules before confirming worker availability.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-border/70">
        <table className="w-full text-sm">
        <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
          {workDayDateLabels.length > 0 ? (
            <tr className="border-b border-border/60">
              <th colSpan={7} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span>Work days</span>
                  {workDayDateLabels.map((workDate) => (
                    <Button
                      key={workDate}
                      type="button"
                      variant={effectiveWorkDateFilter === workDate ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleWorkDateFilterChange(workDate)}
                      className={`h-7 rounded-full px-2.5 text-xs normal-case tracking-normal ${
                        effectiveWorkDateFilter === workDate ? '' : 'bg-background'
                      }`}
                    >
                      {formatDate(workDate)}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="h-7 rounded-full bg-background px-2.5 text-xs normal-case tracking-normal"
                  >
                    <Link to={selectedWorkdayCalendarHref}>
                      <CalendarDays className="mr-1.5 h-3.5 w-3.5" />
                      More
                    </Link>
                  </Button>
                </div>
              </th>
            </tr>
          ) : null}
          <tr>
            <th className="px-4 py-3">Worker</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Hrs Estimated</th>
            <th className="px-4 py-3">Hrs Logged</th>
            <th className="px-4 py-3">Pay Agreed</th>
            <th className="px-4 py-3">Actual Payout</th>
            <th className="px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {visibleEntriesWithAssignment.length > 0 ? visibleEntriesWithAssignment.map(({ entry, assignment }) => {
            const workerId = String(entry.worker ?? '')
            const entryStatus = resolveWorkDayEntryStatusFromEntry(entry)
            const statusLabel = entry.status_label || getWorkDayEntryStatusLabel(entryStatus ?? entry.status)
            const approvedModifierLabels = getApprovedModifierLabels(entry)
            const ratingSummary = formatRatingSummary(entry)
            const showActualPayout = isWorkerInvoicePayType(entry.pay_type || assignment?.pay_type || assignment?.pay_type_snapshot)
            const showClash = !shouldHideClashForAssignment(assignment?.status)
              && !shouldHideClashForWorkdayEntryStatus(entry.status)
              && Number(entry.overlap_conflict_count ?? 0) > 0

            return (
              <tr key={entry.id} className="border-t border-border/60">
                <td className="px-4 py-3">
                  {workerId ? (
                    <Link
                      to={withServiceInstanceId(`/workers/${workerId}`, serviceInstanceId)}
                      className="text-primary hover:underline"
                    >
                      {entry.worker_name || `Worker #${workerId}`}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">Unassigned</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className={getWorkDayEntryStatusBadgeClassName(entryStatus ?? entry.status)}
                    >
                      {statusLabel}
                    </Badge>
                    {entry.needs_supervisor_attention ? (
                      <Badge
                        variant="outline"
                        className="border-amber-300 bg-amber-50 text-[10px] text-amber-800"
                      >
                        Needs review
                      </Badge>
                    ) : null}
                    {entry.is_late ? (
                      <Badge
                        variant="outline"
                        className="border-orange-300 bg-orange-50 text-[10px] text-orange-800"
                      >
                        {formatLateMinutes(entry.late_minutes)}
                      </Badge>
                    ) : null}
                    {showClash ? (
                      <Badge
                        variant="outline"
                        className="gap-1 border-amber-300 bg-amber-50 text-[10px] text-amber-800"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Clash
                      </Badge>
                    ) : null}
                  </div>
                  {showClash && entry.overlap_conflicts?.length ? (
                    <div className="mt-1 space-y-0.5 text-[11px] text-amber-800">
                      {entry.overlap_conflicts.slice(0, 2).map((conflict) => {
                        const start = formatConflictTime(conflict.start)
                        const end = formatConflictTime(conflict.end)
                        const timeLabel = start && end ? `${start} - ${end}` : start || end
                        return (
                          <p key={String(conflict.id)}>
                            Overlaps with{' '}
                            <Link
                              to={withServiceInstanceId(`/job/${conflict.job}`, serviceInstanceId)}
                              className="font-medium underline underline-offset-2"
                            >
                              {conflict.job_title || `${labels.entity} #${conflict.job}`}
                            </Link>
                            {conflict.work_date ? ` on ${formatDate(conflict.work_date)}` : ''}
                            {timeLabel ? `, ${timeLabel}` : ''}.
                          </p>
                        )
                      })}
                    </div>
                  ) : null}
                  {approvedModifierLabels.length > 0 || ratingSummary ? (
                    <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                      {approvedModifierLabels.length > 0 ? (
                        <p>Approved modifiers: {approvedModifierLabels.join(', ')}</p>
                      ) : null}
                      {ratingSummary ? <p>Ratings: {ratingSummary}</p> : null}
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{resolveEstimatedHours(entry)}</td>
                <td className="px-4 py-3 text-muted-foreground">{resolveLoggedHours(entry)}</td>
                <td className="px-4 py-3 text-muted-foreground">{formatPayAgreed(assignment)}</td>
                <td className="px-4 py-3">{showActualPayout ? formatMoney(entry.total_pay) : '-'}</td>
                <td className="relative px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <WorkDayEntryActionsMenu
                      entry={{ ...entry, status: entryStatus ?? entry.status }}
                      serviceInstanceId={serviceInstanceId}
                      assignmentId={assignment?.id}
                      blocked={jobModificationBlocked}
                      assignmentBlocked={isTerminalAssignmentStatus(assignment?.status)}
                      assignmentStatus={assignment?.status}
                      alwaysShowTrigger
                      allowGroupWorkdayAccess={canGroupWorkdayStatusOverride}
                      onStatusChange={(status, options) => {
                        const currentStatus = resolveWorkDayEntryStatusFromEntry(entry)
                        if (status === currentStatus) {
                          return
                        }

                        setWorkDayEntryStatusMutation.mutate({
                          id: String(entry.id),
                          status,
                          assignmentId: assignment?.id,
                          checkIn: options?.checkIn,
                          checkOut: options?.checkOut,
                          reason: options?.reason,
                        })
                      }}
                    />
                  </div>
                </td>
              </tr>
            )
          }) : (
            <tr className="border-t border-border/60">
              <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                No workday entries found for this worker assignment.
              </td>
            </tr>
          )}
          {hiddenEntryCount > 0 ? (
            <tr className="border-t border-border/60 bg-muted/20">
              <td colSpan={7} className="px-4 py-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {visibleEntriesWithAssignment.length} of {filteredEntriesWithAssignment.length} {labels.workDayEntryPluralLower}.
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={workdaysHref}>
                      <CalendarDays className="mr-2 h-4 w-4" />
                      Open calendar
                    </Link>
                  </Button>
                </div>
              </td>
            </tr>
          ) : null}
        </tbody>
        </table>
      </div>
    </div>
  )
}
